import React, { FC, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, TextField } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import ProductionPlanTemplateSteps from './production-plan-template-steps/production-plan-template-steps.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import {
    ProductionPlanningTemplate,
    ProductionPlanningTemplateInput,
    PlanningWorkflowStepInput,
} from 'models/organization';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';

type ProductionPlanTemplateStep = {
    _id: string;
    name: string;
    departmentId: string;
    machineId?: string;
    estimatedProductionHours?: number;
    estimatedMachineHours?: number;
    allowOvernight?: boolean;
    startNextDay?: boolean;
    affectedByQuantityModifier?: number;
};

type AddEditProductionPlanTemplateDialogProps = {
    open: boolean;
    productionTemplate?: ProductionPlanningTemplate;
    onDialogClose: () => void;
    addPlanningTemplate: (variables: ProductionPlanningTemplateInput) => Promise<any>;
    editPlanningTemplate: (variables: ProductionPlanningTemplateInput) => Promise<any>;
    addTemplateLoading: boolean;
    editTemplateLoading: boolean;
};

const AddEditProductionPlanTemplateDialog: FC<AddEditProductionPlanTemplateDialogProps> = ({
    open,
    productionTemplate,
    onDialogClose,
    addPlanningTemplate,
    editPlanningTemplate,
    addTemplateLoading,
    editTemplateLoading,
}) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [name, setName] = useState('');
    const [steps, setSteps] = useState<ProductionPlanTemplateStep[]>([]);
    const [isStepsValid, setIsStepsValid] = useState(true);
    const [hasInvalidTime, setHasInvalidTime] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (productionTemplate?._id) {
            setName(productionTemplate.name || '');
            setSteps(
                (productionTemplate.workflowSteps || []).map((step) => ({
                    ...step,
                    _id: (step as any).stepId || (step as any)._id || '',
                    name: (step as any).name ?? '',
                    estimatedProductionHours: (step as any).estimatedProductionHours ?? 0,
                    estimatedMachineHours: (step as any).estimatedMachineHours ?? 0,
                    allowOvernight: (step as any).allowOvernight ?? false,
                    startNextDay: (step as any).startNextDay ?? false,
                    affectedByQuantityModifier: (step as any).affectedByQuantityModifier ?? 0,
                    departmentId: (step as any).departmentId || '',
                })),
            );
        } else {
            setName('');
            setSteps([]);
        }
        setIsStepsValid(true);
        setHasInvalidTime(false);
    }, [open, productionTemplate]);

    const handleStepsChange = useCallback(
        (
            updatedSteps: ProductionPlanTemplateStep[],
            isValid?: boolean,
            hasStepsWithoutDept?: boolean,
            hasInvalidTimeInputs?: boolean,
        ) => {
            setSteps(updatedSteps);
            setHasInvalidTime(!!hasInvalidTimeInputs);
            setIsStepsValid((isValid ?? true) && !hasStepsWithoutDept && !hasInvalidTimeInputs);
        },
        [],
    );

    const isSubmitDisabled = useMemo(
        () => !name.trim() || steps.length === 0 || !isStepsValid,
        [name, steps.length, isStepsValid],
    );

    const handleSubmit = useCallback(async () => {
        try {
            const templateData = {
                name,
                workflowSteps: steps.map(
                    (step, index): PlanningWorkflowStepInput => ({
                        departmentId: step.departmentId || step._id,
                        stepId: step._id,
                        stepOrder: index + 1,
                        machineId: step.machineId || undefined,
                        estimatedProductionHours: step.estimatedProductionHours || 0,
                        estimatedMachineHours: step.machineId ? undefined : step.estimatedMachineHours || 0,
                        allowOvernight: step.allowOvernight || false,
                        startNextDay: step.startNextDay || false,
                        affectedByQuantityModifier: step.affectedByQuantityModifier ?? 0,
                    }),
                ),
            };

            if (productionTemplate?._id) {
                await editPlanningTemplate({ template: { ...templateData, templateId: productionTemplate._id } });
            } else {
                await addPlanningTemplate({ template: templateData });
            }

            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: productionTemplate?._id
                    ? translate('common:CHANGES_SAVED')
                    : translate('lab-settings:PRODUCTION_PLAN_TEMPLATE_ADDED_SUCCESSFULLY'),
            });

            onDialogClose();
        } catch (error) {
            console.error('Error saving production plan template:', error);
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: translate('common:ERROR_OCCURRED'),
            });
        }
    }, [
        name,
        steps,
        productionTemplate,
        addPlanningTemplate,
        editPlanningTemplate,
        setAppToasterContent,
        translate,
        onDialogClose,
    ]);

    return (
        <DHDialog
            title={
                productionTemplate?._id
                    ? translate('lab-settings:EDIT_PRODUCTION_PLAN_TEMPLATE')
                    : translate('lab-settings:ADD_PRODUCTION_PLAN')
            }
            maxWidth="xl"
            fullWidth
            open={open}
            onClose={onDialogClose}
            height="450px"
            actions={
                <>
                    {hasInvalidTime && (
                        <Alert severity="error" sx={{ mr: 1 }}>
                            {translate('lab-settings:INVALID_TIME_FORMAT_HINT')}
                        </Alert>
                    )}
                    <LoadingButton
                        disabled={isSubmitDisabled}
                        variant="contained"
                        type="submit"
                        loading={productionTemplate?._id ? editTemplateLoading : addTemplateLoading}
                        onClick={handleSubmit}
                    >
                        {productionTemplate?._id ? translate('common:UPDATE') : translate('common:ADD')}
                    </LoadingButton>
                </>
            }
        >
            <Box mt={1}>
                <Box mb={2}>
                    <TextField
                        fullWidth
                        label={translate('lab-settings:PRODUCTION_PLAN_TEMPLATE_NAME')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        variant="outlined"
                        size="small"
                        data-test="production-plan-template-name-input"
                    />
                </Box>

                <ProductionPlanTemplateSteps
                    key={productionTemplate?._id ?? 'new'}
                    productionTemplate={productionTemplate ?? null}
                    onStepsChange={handleStepsChange}
                />
            </Box>
        </DHDialog>
    );
};

export default AddEditProductionPlanTemplateDialog;
